import { ApolloCache, ApolloError } from "@apollo/client";

/**
 * Guard codes the backend returns when a referenced company/asset is gone.
 * `*_DELETED` means "it went away under you, refresh"; `*_NOT_FOUND` means a
 * bad id — deliberately different copy.
 */
export type EntityStateErrorCode =
  | "COMPANY_DELETED"
  | "COMPANY_NOT_FOUND"
  | "ASSET_DELETED"
  | "ASSET_NOT_FOUND";

const ENTITY_STATE_ERROR_CODES: EntityStateErrorCode[] = [
  "COMPANY_DELETED",
  "COMPANY_NOT_FOUND",
  "ASSET_DELETED",
  "ASSET_NOT_FOUND",
];

export interface EntityStateError {
  code: EntityStateErrorCode;
  /**
   * Where to render the error — *not* necessarily a key the caller sent. On
   * the linked-create paths this points at the company slot even though the
   * company came from the linked record; see `sourceField`.
   */
  field?: string;
  /**
   * Present only when the rejected value came from a *linked source record*
   * rather than from our payload — `createSupportTicket(workOrderId)` and
   * `createTicket(supportTicketId)`, whose source company was soft-deleted.
   * Absent (key omitted entirely) on plain user-input rejections, so a
   * truthiness check is safe.
   *
   * It matters because the company slot is prefilled and disabled in those
   * flows, so an inline error on `field` would be invisible — render a
   * form-level message instead. `companyId` carries the dead company's id.
   */
  sourceField?: string;
  companyId?: string;
  assetIds?: string[];
  deletedAssetIds?: string[];
  missingAssetIds?: string[];
}

/**
 * Extracts the company/asset guard codes from an `InvalidInputError`.
 *
 * The guard code lives at `extensions.additionalArgs.code` — mind the three
 * different things called "code" on these payloads:
 *   - `extensions.additionalArgs.code` → the guard code (what we want)
 *   - `extensions.code`                → the constant `"INVALID_INPUT_ERROR"`
 *   - top-level `code`                 → the numeric HTTP status (400)
 *
 * There is no `extensions.fields` on these, so `errorsHandler` cannot route
 * them onto field errors (it only spreads `extensions.fields`, and only for
 * `BAD_USER_INPUT`) — every one of them would otherwise collapse into a
 * generic toast. Call this first and fall back to `errorsHandler`.
 *
 * Match on the code, never on `message`: the codes are stable, the copy isn't.
 */
export const parseEntityStateError = (
  error: unknown,
): EntityStateError | null => {
  const args = (error as ApolloError)?.graphQLErrors?.[0]?.extensions
    ?.additionalArgs as Record<string, unknown> | undefined;
  const code = args?.code as EntityStateErrorCode | undefined;

  if (!code || !ENTITY_STATE_ERROR_CODES.includes(code)) return null;

  return {
    code,
    field: typeof args?.field === "string" ? args.field : undefined,
    sourceField:
      typeof args?.sourceField === "string" ? args.sourceField : undefined,
    companyId: typeof args?.companyId === "string" ? args.companyId : undefined,
    assetIds: Array.isArray(args?.assetIds)
      ? (args.assetIds as string[])
      : undefined,
    deletedAssetIds: Array.isArray(args?.deletedAssetIds)
      ? (args.deletedAssetIds as string[])
      : undefined,
    missingAssetIds: Array.isArray(args?.missingAssetIds)
      ? (args.missingAssetIds as string[])
      : undefined,
  };
};

/**
 * Ticket creation at the OEM's open-ticket limit fails with
 * `extensions.additionalArgs = { code: "OPEN_TICKET_LIMIT_REACHED", limit }`.
 * Returns the limit, or null for any other error.
 */
export const parseOpenTicketLimitError = (error: unknown): number | null => {
  const args = (error as ApolloError)?.graphQLErrors?.[0]?.extensions
    ?.additionalArgs as Record<string, unknown> | undefined;
  if (args?.code !== "OPEN_TICKET_LIMIT_REACHED") return null;
  return typeof args.limit === "number" ? args.limit : 0;
};

export const isNotFoundError = (error: ApolloError) => {
  return (
    error.graphQLErrors?.[0]?.extensions?.code === "NOT_FOUND" ||
    error.networkError?.["statusCode"] === 404 ||
    error?.graphQLErrors[0]?.extensions?.exception?.code
  );
};

/**
 * Root query fields whose results a company-hierarchy or portal change
 * invalidates. Such a change ripples well past the record being edited: the
 * backend mirrors company-hierarchy edges onto the portal hierarchy (creating
 * or deleting portals across the whole subtree) and recomputes the
 * denormalised descendant counts.
 */
const HIERARCHY_ROOT_FIELDS = [
  "getOwnOemCustomerById",
  "listAllOwnOemCustomers",
  "listOwnOemKanbanCustomers",
  "listCustomerPortals",
  "getCustomerPortal",
];

/** Company fields that a hierarchy or portal change can move. */
const HIERARCHY_COMPANY_FIELDS = [
  "parents",
  "accessibleParents",
  "hierarchy",
  "childrenCount",
  "totalChildrenCount",
  "hasCustomerPortal",
  "parentsWithPortalCount",
];

/**
 * Drop everything a company-hierarchy or portal change can affect, so companies
 * and portals are read back from the server.
 *
 * Evicting the root fields alone is not enough: the normalised `Customer`
 * entities survive it, so a cached read could still serve the pre-change
 * `parents` / counts / `hasCustomerPortal`. Any company ids known to be
 * involved therefore also get those specific fields invalidated — `name`, ids
 * and the rest stay put, so nothing flickers to blank while refetching.
 */
export const evictHierarchyCaches = (
  cache: ApolloCache<unknown>,
  companyIds: (string | undefined)[] = [],
) => {
  HIERARCHY_ROOT_FIELDS.forEach((fieldName) =>
    cache.evict({ id: "ROOT_QUERY", fieldName }),
  );

  companyIds.filter(Boolean).forEach((companyId) => {
    cache.modify({
      id: cache.identify({ __typename: "Customer", _id: companyId }),
      fields: HIERARCHY_COMPANY_FIELDS.reduce(
        (fields, field) => ({ ...fields, [field]: (_, { DELETE }) => DELETE }),
        {},
      ),
    });
  });

  cache.gc();
};
